import { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import { StockData } from '../data/sampleData';

interface CombinedVisualizationProps {
  stocksData: StockData[];
}

const CombinedVisualization = ({ stocksData }: CombinedVisualizationProps) => {
  const [chartData, setChartData] = useState<any[]>([]);
  
  useEffect(() => {
    if (stocksData.length > 0) {
      // Get the primary stock for reference
      const primaryStock = stocksData[0];
      
      if (!primaryStock.data || primaryStock.data.length === 0) {
        return;
      }
      
      // Create combined chart data
      const combined = primaryStock.data.map((dataPoint, index) => {
        const entry: any = {
          date: dataPoint.date,
          [primaryStock.symbol]: dataPoint.value,
        };
        
        // Add predictions for primary if available
        if (primaryStock.prediction && index < primaryStock.prediction.length) {
          entry[`${primaryStock.symbol}_predicted`] = primaryStock.prediction[index].value;
        }
        
        // Add other stocks if available at similar date points
        stocksData.slice(1).forEach(stock => {
          if (!stock.data || stock.data.length === 0) return;
          
          const matchingPoint = stock.data.find(p => p.date === dataPoint.date);
          if (matchingPoint) {
            entry[stock.symbol] = matchingPoint.value;
            
            // Normalize to primary stock scale for overlaid visualization
            const ratio = primaryStock.currentValue / stock.currentValue;
            const normalizedValue = matchingPoint.value * ratio;
            if (!isNaN(normalizedValue)) {
              entry[`${stock.symbol}_normalized`] = normalizedValue;
            }
          }
        });
        
        return entry;
      });
      
      // Add prediction data after actual data if available
      if (primaryStock.prediction && primaryStock.prediction.length > 0) {
        const lastActualDate = new Date(primaryStock.data[primaryStock.data.length - 1].date);
        
        primaryStock.prediction.forEach(predPoint => {
          const predDate = new Date(predPoint.date);
          
          // Only add prediction points that are after the last actual data point
          if (predDate > lastActualDate) {
            const entry: any = {
              date: predPoint.date,
              [`${primaryStock.symbol}_predicted`]: predPoint.value,
            };
            combined.push(entry);
          }
        });
      }
      
      setChartData(combined);
    }
  }, [stocksData]);
  
  if (!chartData.length) {
    return (
      <div className="w-full bg-card border rounded-xl overflow-hidden p-6 h-[500px]">
        <h3 className="text-xl font-semibold mb-4">Combined Data Visualization</h3>
        <div className="flex items-center justify-center h-[400px]">
          <p className="text-muted-foreground">No data available for combined visualization</p>
        </div>
      </div>
    );
  }
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border p-3 rounded-md shadow-sm">
          <p className="label text-sm font-medium">{`Date: ${new Date(label).toLocaleDateString()}`}</p>
          <div className="flex flex-col gap-1 mt-1">
            {payload.map((entry: any, index: number) => (
              <p key={`item-${index}`} className="text-xs">
                <span className="font-medium" style={{ color: entry.color }}>
                  {entry.name}: 
                </span>
                <span className="ml-1">
                  {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
                </span>
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Find the index where predictions start
  const findPredictionStartIndex = () => {
    const primaryStock = stocksData[0];
    if (!primaryStock.prediction || primaryStock.prediction.length === 0) {
      return -1;
    }
    
    const firstPredictionDate = primaryStock.prediction[0].date;
    return chartData.findIndex(d => d.date === firstPredictionDate);
  };

  const predictionStartIndex = findPredictionStartIndex();

  return (
    <div className="w-full bg-card border rounded-xl overflow-hidden p-6 h-[500px]">
      <h3 className="text-xl font-semibold mb-4">Combined Data Visualization</h3>
      <ResponsiveContainer width="100%" height="85%">
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        >
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis 
            dataKey="date" 
            scale="time"
            tick={{ fontSize: 12 }}
            tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { year: '2-digit', month: 'short' })}
          />
          <YAxis yAxisId="left" orientation="left" />
          {stocksData.length > 1 && (
            <YAxis yAxisId="right" orientation="right" />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {/* Primary stock */}
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey={stocksData[0].symbol} 
            stroke="#3b82f6" 
            strokeWidth={2}
            dot={false}
            name={`${stocksData[0].symbol} (Actual)`}
          />
          
          {/* Primary stock prediction */}
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey={`${stocksData[0].symbol}_predicted`} 
            stroke="#8b5cf6" 
            strokeDasharray="5 5"
            strokeWidth={1.5}
            dot={false}
            name={`${stocksData[0].symbol} (Predicted)`}
          />
          
          {/* Add reference line at prediction start if available */}
          {predictionStartIndex > 0 && (
            <ReferenceLine 
              x={chartData[predictionStartIndex].date} 
              stroke="#8b5cf6" 
              strokeDasharray="3 3"
              label={{ value: 'Prediction Start', position: 'top', fill: '#8b5cf6' }}
            />
          )}
          
          {/* Other stocks */}
          {stocksData.slice(1).map((stock, index) => (
            <Line 
              key={stock.id}
              yAxisId={index === 0 ? "right" : "left"}
              type="monotone" 
              dataKey={`${stock.symbol}_normalized`} 
              stroke={index === 0 ? "#ef4444" : "#10b981"} 
              strokeWidth={1.5}
              dot={false}
              name={`${stock.symbol} (Normalized)`}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CombinedVisualization;
